<template>
  <v-container fluid class="wa">
    <div class="wa-shell">
      <nav class="wa-breadcrumb" aria-label="Nawigacja Wiki">
        <button type="button" class="wa-back" @click="goBack">
          <v-icon size="16">mdi-arrow-left</v-icon>
          <span>{{ $t('wiki.backToWiki') }}</span>
        </button>
        <v-icon size="15">mdi-chevron-right</v-icon>
        <span>{{ sectionLabel || 'Artykuł' }}</span>
      </nav>

      <header v-if="article" class="wa-header">
        <div class="wa-heading">
          <span class="wa-file-icon" :style="{ '--wa-file-color': typeColor(article.mainFileType) }" aria-hidden="true">
            <v-icon size="27">{{ fileIcon(article.mainFileType) }}</v-icon>
          </span>
          <div class="wa-title-wrap">
            <div class="wa-eyebrow">
              <span :style="{ color: typeColor(article.mainFileType) }">{{ (article.mainFileType || 'FILE').toUpperCase() }}</span>
              <i></i>
              <span>Baza wiedzy BTP</span>
            </div>
            <h1 class="wa-title">{{ article.title }}</h1>
            <div class="wa-meta">
              <span><v-icon size="14">mdi-folder-outline</v-icon>{{ sectionLabel }}</span>
              <span v-if="article.mainFileSize"><v-icon size="14">mdi-database-outline</v-icon>{{ article.mainFileSize }}</span>
              <span v-if="article.createdAt"><v-icon size="14">mdi-calendar-outline</v-icon>{{ formatDate(article.createdAt) }}</span>
            </div>
          </div>
        </div>
        <div class="wa-actions">
          <v-btn class="wa-action wa-action--primary" depressed :href="mainDownloadSrc" :disabled="!mainFileSrc" target="_blank" rel="noopener">
            <v-icon size="17" left>mdi-download</v-icon>{{ $t('common.download') }}
          </v-btn>
          <v-btn class="wa-action wa-action--secondary" outlined @click="openMainInNewTab" :disabled="!mainFileSrc">
            <v-icon size="17" left>mdi-open-in-new</v-icon>{{ $t('wiki.newTab') }}
          </v-btn>
          <v-btn v-if="canManage && article.content" class="wa-action wa-action--secondary" outlined @click="openEditNoteArticle">
            <v-icon size="17" left>mdi-note-edit-outline</v-icon>Edytuj notatkę
          </v-btn>
          <v-btn v-if="canManage" class="wa-action wa-action--danger" outlined @click="confirmDeleteArticle">
            <v-icon size="17" left>mdi-trash-can-outline</v-icon>{{ $t('common.delete') }}
          </v-btn>
        </div>
      </header>

      <div v-else-if="loading" class="wa-header wa-header--loading">
        <div class="wa-heading">
          <span class="wa-file-icon"><v-progress-circular indeterminate size="24" width="3" color="#7896FF" /></span>
          <div class="wa-title-wrap"><div class="wa-eyebrow">Baza wiedzy BTP</div><h1 class="wa-title">Ładuję artykuł…</h1></div>
        </div>
      </div>

    <div class="wa-body">
      <section class="wa-viewer-col" aria-label="Podgląd dokumentu">
        <div class="wa-viewer-toolbar">
          <div class="wa-viewer-file">
            <span><v-icon size="17">{{ article ? fileIcon(article.mainFileType) : 'mdi-file-outline' }}</v-icon></span>
            <div>
              <small>Podgląd dokumentu</small>
              <strong :title="article && article.mainFileName">{{ article && article.mainFileName ? article.mainFileName : 'Dokument główny' }}</strong>
            </div>
          </div>
          <div v-if="article && mainFileSrc" class="wa-viewer-status"><span></span>Plik dostępny</div>
        </div>
        <div v-if="loading" class="wa-state">
          <div class="wa-state-orb"><v-progress-circular indeterminate size="30" width="3" color="#7896FF" /></div>
          <strong>Ładuję dokument</strong>
          <span>{{ $t('common.loading') }}</span>
        </div>
        <div v-else-if="error" class="wa-state wa-state--error">
          <div class="wa-state-orb"><v-icon size="34">mdi-alert-circle-outline</v-icon></div>
          <strong>Nie udało się otworzyć materiału</strong>
          <span>{{ error }}</span>
          <v-btn small color="primary" depressed @click="fetchArticle" class="mt-3">{{ $t('wiki.tryAgain') }}</v-btn>
        </div>
        <PdfViewer
          v-else-if="article && isPdf(article.mainFileType) && mainFileSrc"
          :src="mainFileSrc"
          class="wa-pdf"
        />
        <div v-else-if="article && isImage(article.mainFileType) && mainFileSrc" class="wa-image">
          <img :src="mainFileSrc" :alt="article.title">
        </div>
        <div v-else class="wa-state">
          <div class="wa-state-orb"><v-icon size="34">mdi-file-eye-outline</v-icon></div>
          <strong>{{ $t('wiki.previewNotAvailable') }}</strong>
          <span>Pobierz plik albo otwórz go w nowej karcie.</span>
          <v-btn v-if="mainFileSrc" small color="primary" depressed :href="mainFileSrc" target="_blank" rel="noopener" class="mt-3">
            <v-icon small left>mdi-open-in-new</v-icon>{{ $t('wiki.openInNewTab') }}
          </v-btn>
        </div>
      </section>

      <aside
        class="wa-side"
        :class="{ 'wa-side--drag': attDragDepth > 0 && canManage }"
        @dragover.prevent
        @dragenter.prevent="attDragDepth++"
        @dragleave.prevent="attDragDepth = Math.max(0, attDragDepth - 1)"
        @drop.prevent="onAttDrop"
      >
        <div class="wa-side-head">
          <div class="wa-side-head-title">
            <span class="wa-side-head-icon"><v-icon size="19">mdi-paperclip</v-icon></span>
            <div><small>Materiały dodatkowe</small><strong>{{ $t('wiki.attachments') }}</strong></div>
          </div>
          <span class="wa-side-count">{{ attachments.length }}</span>
        </div>

        <div class="wa-att-list">
          <div v-if="attachments.length === 0" class="wa-att-empty">
            <span><v-icon size="28">mdi-folder-open-outline</v-icon></span>
            <strong>{{ $t('wiki.noAttachments') }}</strong>
            <small>Dodatkowe pliki pojawią się w tym miejscu.</small>
          </div>
          <article
            v-for="att in attachments"
            :key="att.id"
            class="wa-att"
            :style="{ '--wa-file-color': typeColor(att.fileType) }"
          >
            <div class="wa-att-row">
              <button type="button" class="wa-att-main" @click="openAttachment(att)">
                <span class="wa-att-icon"><v-icon size="21">{{ fileIcon(att.fileType) }}</v-icon></span>
                <span class="wa-att-text">
                  <strong class="wa-att-name" :title="att.name">{{ att.name }}</strong>
                  <small class="wa-att-meta">
                    <span>{{ (att.fileType || 'FILE').toUpperCase() }}</span>
                    <span v-if="att.fileSize">{{ att.fileSize }}</span>
                    <span v-if="att.createdAt">{{ formatDate(att.createdAt) }}</span>
                  </small>
                </span>
              </button>
              <div class="wa-att-actions">
                <v-btn icon small @click.stop="openAttachment(att)" :title="$t('common.preview')" :aria-label="$t('common.preview') + ': ' + att.name">
                  <v-icon size="17">mdi-eye-outline</v-icon>
                </v-btn>
                <v-btn v-if="canManage && att.noteContent" icon small @click.stop="openEditNoteAtt(att)" title="Edytuj notatkę" :aria-label="'Edytuj notatkę: ' + att.name">
                  <v-icon size="17">mdi-note-edit-outline</v-icon>
                </v-btn>
                <v-btn v-if="canManage" icon small @click.stop="startEditAtt(att)" :title="att.description ? $t('wiki.editDescription') : $t('wiki.addDescription')" :aria-label="(att.description ? $t('wiki.editDescription') : $t('wiki.addDescription')) + ': ' + att.name">
                  <v-icon size="17">mdi-text-box-edit-outline</v-icon>
                </v-btn>
                <v-btn icon small :href="attachmentDownloadSrc(att)" target="_blank" rel="noopener" @click.stop :title="$t('common.download')" :aria-label="$t('common.download') + ': ' + att.name">
                  <v-icon size="17">mdi-download</v-icon>
                </v-btn>
                <v-btn v-if="canManage" icon small class="wa-att-del" @click.stop="removeAttachment(att)" :title="$t('common.delete')" :aria-label="$t('common.delete') + ': ' + att.name">
                  <v-icon size="17">mdi-trash-can-outline</v-icon>
                </v-btn>
              </div>
            </div>
            <div v-if="editingAttId === att.id" class="wa-att-edit">
              <v-textarea
                v-model="editAttDesc"
                :placeholder="$t('wiki.descriptionPlaceholder')"
                rows="2" auto-grow outlined dense hide-details
              ></v-textarea>
              <div class="wa-att-edit-actions">
                <v-btn x-small color="primary" :loading="savingAtt" @click="saveAttDescription(att)">{{ $t('common.save') }}</v-btn>
                <v-btn x-small text @click="cancelEditAtt">{{ $t('common.cancel') }}</v-btn>
              </div>
            </div>
            <div v-else-if="att.description" class="wa-att-desc">{{ att.description }}</div>
          </article>
        </div>

        <v-btn
          v-if="canManage"
          block
          depressed
          color="primary"
          :loading="uploadingAtt"
          class="wa-att-add"
          @click="$refs.attInput.click()"
        >
          <v-icon small left>mdi-plus</v-icon>{{ $t('wiki.addAttachment') }}
        </v-btn>
        <v-btn
          v-if="canManage"
          block
          outlined
          color="primary"
          class="wa-att-add mt-2"
          @click="openNewNoteAtt"
        >
          <v-icon small left>mdi-note-plus-outline</v-icon>Notatka PDF
        </v-btn>
        <input
          ref="attInput"
          type="file"
          multiple
          style="display:none"
          @change="onAttPick"
        >
      </aside>
    </div>

    <!-- Notatka (nowa przy artykule / edycja artykułu-notatki / edycja załącznika-notatki) -->
    <v-dialog v-model="noteDialog" max-width="640" persistent>
      <v-card dark class="wa-dialog-card">
        <v-card-title class="wa-dialog-title">
          <span class="wa-dialog-file"><v-icon>mdi-note-edit-outline</v-icon></span>
          <span class="wa-dialog-copy"><small>Notatka PDF</small><strong>{{ noteMode === 'new-att' ? 'Nowa notatka przy artykule' : 'Edycja notatki' }}</strong></span>
        </v-card-title>
        <v-card-text>
          <v-text-field v-model="noteForm.title" label="Tytuł" outlined dense autofocus />
          <v-textarea
            v-model="noteForm.content"
            label="Treść"
            placeholder="Treść notatki — zostanie zapisana jako PDF."
            outlined rows="10" auto-grow
          />
          <div class="text-caption grey--text">Po zapisaniu PDF wygeneruje się na nowo z tej treści.</div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text :disabled="noteSaving" @click="noteDialog = false">{{ $t('common.cancel') }}</v-btn>
          <v-btn color="primary" depressed :loading="noteSaving" :disabled="!noteForm.title.trim() || !noteForm.content.trim()" @click="submitNote">{{ $t('common.save') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Podgląd załącznika -->
    <v-dialog v-model="attPreview" max-width="940" @click:outside="closeAttPreview">
      <v-card v-if="previewAtt" dark class="wa-dialog-card wa-preview-dialog">
        <v-card-title class="wa-dialog-title">
          <span class="wa-dialog-file"><v-icon>{{ fileIcon(previewAtt.fileType) }}</v-icon></span>
          <span class="wa-dialog-copy"><small>Podgląd załącznika</small><strong>{{ previewAtt.name }}</strong></span>
          <div class="wa-dialog-head-actions">
            <v-btn small depressed color="primary" :href="attachmentDownloadSrc(previewAtt)" target="_blank" rel="noopener">
              <v-icon small left>mdi-download</v-icon>{{ $t('common.download') }}
            </v-btn>
            <v-btn icon aria-label="Zamknij podgląd" @click="closeAttPreview"><v-icon>mdi-close</v-icon></v-btn>
          </div>
        </v-card-title>
        <v-card-text class="wa-preview-content">
          <PdfViewer
            v-if="isPdf(previewAtt.fileType) && previewAttSrc"
            :src="previewAttSrc"
            class="wa-preview-pdf"
          />
          <div v-else-if="isImage(previewAtt.fileType) && previewAttSrc" class="wa-preview-img">
            <img :src="previewAttSrc" :alt="previewAtt.name">
          </div>
          <div v-else class="wa-state">
            <v-icon large color="grey">mdi-file-outline</v-icon>
            <div>{{ $t('wiki.noPreviewForType') }}</div>
            <v-btn small color="primary" depressed :href="previewAttSrc" target="_blank" rel="noopener" class="mt-3">
              <v-icon small left>mdi-open-in-new</v-icon>{{ $t('wiki.open') }}
            </v-btn>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Potwierdzenie usunięcia artykułu -->
    <v-dialog v-model="deleteDialog" max-width="430">
      <v-card dark class="wa-dialog-card">
        <v-card-title class="wa-dialog-title"><span class="wa-dialog-file wa-dialog-file--danger"><v-icon>mdi-trash-can-outline</v-icon></span><span class="wa-dialog-copy"><small>Operacja nieodwracalna</small><strong>{{ $t('wiki.confirmation') }}</strong></span></v-card-title>
        <v-card-text>{{ $t('wiki.confirmDeleteArticle') }} „{{ article && article.title }}"? {{ $t('wiki.attachmentsWillBeDeleted') }}</v-card-text>
        <v-card-actions class="wa-dialog-footer">
          <v-spacer />
          <v-btn text @click="deleteDialog = false">{{ $t('common.cancel') }}</v-btn>
          <v-btn color="error" depressed @click="executeDeleteArticle">{{ $t('common.delete') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    </div>
  </v-container>
</template>

<script>
const axios = require('axios');
import PdfViewer from '@/components/PdfViewer.vue';
import { isSuperAdmin as isSuperAdminFn } from '@/services/permissions';

export default {
  name: 'WikiArticlePage',

  components: { PdfViewer },

  data: () => ({
    article: null,
    sections: [],
    loading: true,
    error: '',
    uploadingAtt: false,
    attDragDepth: 0,
    editingAttId: null,
    editAttDesc: '',
    noteDialog: false,
    noteSaving: false,
    noteMode: 'new-att', // 'new-att' | 'edit-article' | 'edit-att'
    noteAttId: null,
    noteForm: { title: '', content: '' },
    savingAtt: false,
    attPreview: false,
    previewAtt: null,
    deleteDialog: false,
  }),

  computed: {
    canManage() {
      if (isSuperAdminFn()) return true;
      try {
        const edits = JSON.parse(localStorage.getItem('pagesWithEdit') || '[]');
        return Array.isArray(edits) && edits.includes('wiki');
      } catch (e) { return false; }
    },
    articleId() {
      return this.$route.params.id;
    },
    token() {
      return localStorage.getItem('token');
    },
    mainFileSrc() {
      if (!this.article || !this.article.mainFileUrl) return '';
      // updatedAt jako cache-buster — po edycji notatki viewer musi wziąć świeży PDF.
      const v = this.article.updatedAt ? '&v=' + encodeURIComponent(this.article.updatedAt) : '';
      return this.apiUrl('wiki/articles/' + this.article.id + '/file?token=' + encodeURIComponent(this.token) + v);
    },
    mainDownloadSrc() {
      if (!this.article || !this.article.mainFileUrl) return '';
      return this.apiUrl('wiki/articles/' + this.article.id + '/file?download=1&token=' + encodeURIComponent(this.token));
    },
    attachments() {
      return (this.article && this.article.attachments) || [];
    },
    previewAttSrc() {
      if (!this.previewAtt) return '';
      return this.apiUrl('wiki/attachments/' + this.previewAtt.id + '/file?token=' + encodeURIComponent(this.token));
    },
    sectionLabel() {
      if (!this.article) return '';
      const s = this.sections.find(x => x.sectionId === this.article.sectionId);
      return s ? s.label : this.article.sectionId;
    },
  },

  watch: {
    articleId: 'fetchArticle',
  },

  mounted() {
    this.fetchArticle();
    this.fetchSections();
  },

  methods: {
    authHeaders() {
      return { Authorization: 'Bearer ' + localStorage.getItem('token') };
    },
    apiUrl(path) {
      return process.env.VUE_APP_ROOT_API + path;
    },
    isPdf(t) { return t === 'pdf'; },
    isImage(t) { return t === 'img'; },
    typeColor(t) {
      const c = { pdf: '#FF7373', docx: '#64B5F6', xlsx: '#42D787', img: '#A78BFA' };
      return c[t] || '#8EA7FF';
    },
    fileIcon(type) {
      const icons = {
        pdf: 'mdi-file-pdf-box',
        docx: 'mdi-file-word-box',
        xlsx: 'mdi-file-excel-box',
        img: 'mdi-file-image-outline',
      };
      return icons[type] || 'mdi-file-document-outline';
    },
    formatDate(iso) {
      if (!iso) return '';
      try {
        return new Date(iso).toLocaleString('pl-PL', {
          year: 'numeric', month: '2-digit', day: '2-digit',
          hour: '2-digit', minute: '2-digit',
        });
      } catch (e) { return iso; }
    },
    async fetchArticle() {
      this.loading = true;
      this.error = '';
      try {
        const res = await axios({
          method: 'get',
          url: this.apiUrl('wiki/articles/' + this.articleId),
          headers: this.authHeaders(),
        });
        this.article = res.data;
      } catch (e) {
        this.error = (e.response && e.response.status === 404)
          ? 'Nie znaleziono artykułu.'
          : 'Nie udało się wczytać artykułu.';
      } finally {
        this.loading = false;
      }
    },
    async fetchSections() {
      try {
        const res = await axios({
          method: 'get',
          url: this.apiUrl('wiki/sections'),
          headers: this.authHeaders(),
        });
        this.sections = res.data || [];
      } catch (e) { this.sections = []; }
    },
    goBack() {
      this.$router.push({ path: '/wiki' }).catch(() => {});
    },
    openMainInNewTab() {
      if (this.mainFileSrc) window.open(this.mainFileSrc, '_blank', 'noopener');
    },
    attachmentDownloadSrc(att) {
      if (!att) return '';
      return this.apiUrl('wiki/attachments/' + att.id + '/file?download=1&token=' + encodeURIComponent(this.token));
    },
    onAttDrop(e) {
      this.attDragDepth = 0;
      if (!this.canManage) return;
      const files = [...(e.dataTransfer?.files || [])];
      if (files.length) this.uploadAttachments(files);
    },
    onAttPick(e) {
      const files = [...(e.target.files || [])];
      e.target.value = '';
      if (files.length) this.uploadAttachments(files);
    },
    async uploadAttachments(files) {
      this.uploadingAtt = true;
      const failed = [];
      for (const file of files) {
        try {
          const fd = new FormData();
          fd.append('file', file);
          await axios({
            method: 'post',
            url: this.apiUrl('wiki/articles/' + this.articleId + '/attachments'),
            data: fd,
            headers: { ...this.authHeaders(), 'Content-Type': 'multipart/form-data' },
          });
        } catch (e) {
          console.error('Attachment upload failed:', e);
          failed.push(file.name);
        }
      }
      await this.fetchArticle();
      if (failed.length) {
        alert('Nie udało się dodać: ' + failed.join(', '));
      }
      this.uploadingAtt = false;
    },
    startEditAtt(att) {
      this.editingAttId = att.id;
      this.editAttDesc = att.description || '';
    },
    cancelEditAtt() {
      this.editingAttId = null;
      this.editAttDesc = '';
    },
    openNewNoteAtt() {
      this.noteMode = 'new-att';
      this.noteAttId = null;
      this.noteForm = { title: '', content: '' };
      this.noteDialog = true;
    },
    openEditNoteArticle() {
      this.noteMode = 'edit-article';
      this.noteAttId = null;
      this.noteForm = { title: this.article.title, content: this.article.content || '' };
      this.noteDialog = true;
    },
    openEditNoteAtt(att) {
      this.noteMode = 'edit-att';
      this.noteAttId = att.id;
      this.noteForm = {
        title: (att.name || '').replace(/\.pdf$/i, ''),
        content: att.noteContent || '',
      };
      this.noteDialog = true;
    },
    async submitNote() {
      this.noteSaving = true;
      try {
        const payload = { title: this.noteForm.title.trim(), content: this.noteForm.content };
        if (this.noteMode === 'new-att') {
          await axios.post(this.apiUrl('wiki/articles/' + this.articleId + '/note'), payload, { headers: this.authHeaders() });
        } else if (this.noteMode === 'edit-article') {
          await axios.put(this.apiUrl('wiki/articles/' + this.articleId + '/note'), payload, { headers: this.authHeaders() });
        } else {
          await axios.put(this.apiUrl('wiki/articles/' + this.articleId + '/attachments/' + this.noteAttId + '/note'), payload, { headers: this.authHeaders() });
        }
        this.noteDialog = false;
        await this.fetchArticle();
      } catch (e) {
        console.error('Note save failed:', e);
        alert(e.response?.data?.message || 'Nie udało się zapisać notatki');
      }
      this.noteSaving = false;
    },
    async saveAttDescription(att) {
      this.savingAtt = true;
      try {
        await axios({
          method: 'put',
          url: this.apiUrl('wiki/articles/' + this.articleId + '/attachments/' + att.id),
          data: { description: this.editAttDesc },
          headers: this.authHeaders(),
        });
        this.editingAttId = null;
        this.editAttDesc = '';
        await this.fetchArticle();
      } catch (e) {
        console.error('Save attachment description failed:', e);
        alert(e.response?.data?.message || 'Nie udało się zapisać opisu');
      }
      this.savingAtt = false;
    },
    openAttachment(att) {
      this.previewAtt = att;
      this.attPreview = true;
    },
    closeAttPreview() {
      this.attPreview = false;
      this.previewAtt = null;
    },
    async removeAttachment(att) {
      if (!confirm('Usunąć załącznik „' + att.name + '"?')) return;
      try {
        await axios({
          method: 'delete',
          url: this.apiUrl('wiki/articles/' + this.articleId + '/attachments/' + att.id),
          headers: this.authHeaders(),
        });
        await this.fetchArticle();
      } catch (e) {
        console.error('Attachment delete failed:', e);
        alert('Nie udało się usunąć załącznika');
      }
    },
    confirmDeleteArticle() {
      this.deleteDialog = true;
    },
    async executeDeleteArticle() {
      try {
        await axios({
          method: 'delete',
          url: this.apiUrl('wiki/articles/' + this.articleId),
          headers: this.authHeaders(),
        });
        this.deleteDialog = false;
        this.$router.push({ path: '/wiki' }).catch(() => {});
      } catch (e) {
        console.error('Article delete failed:', e);
        alert('Nie udało się usunąć artykułu');
      }
    },
  },
};
</script>

<style scoped>
.wa-side--drag { outline: 2px dashed #1f6eb8; outline-offset: 4px; border-radius: 12px; background: rgba(230, 240, 249, 0.5); }
/* ── BTP Knowledge Workspace ─────────────────────────────────────────── */
.wa {
  --wa-bg: #171B26;
  --wa-surface: #232838;
  --wa-surface-soft: #1F2434;
  --wa-surface-hover: #292F40;
  --wa-border: rgba(255, 255, 255, 0.075);
  --wa-border-strong: rgba(126, 151, 255, 0.24);
  --wa-text: #F5F7FC;
  --wa-muted: #AEB8CA;
  --wa-subtle: #8B96AA;
  --wa-blue: #5B7CFF;
  min-height: calc(100vh - 56px);
  margin: -24px;
  padding: 24px clamp(16px, 2.2vw, 34px) 52px;
  background:
    radial-gradient(circle at 7% -8%, rgba(91, 124, 255, 0.16), transparent 29%),
    radial-gradient(circle at 93% 3%, rgba(167, 139, 250, 0.08), transparent 25%),
    var(--wa-bg);
  color: var(--wa-text);
}
.wa * { box-sizing: border-box; }
.wa-shell { width: 100%; max-width: 1680px; margin: 0 auto; }

.wa-breadcrumb { display: flex; min-height: 34px; align-items: center; gap: 7px; margin-bottom: 10px; color: #97A2B4; font-size: 10.5px; font-weight: 650; }
.wa-breadcrumb > .v-icon { color: #5E687B !important; }
.wa-back { display: inline-flex; min-height: 34px; align-items: center; gap: 7px; padding: 5px 9px; border: 1px solid transparent; border-radius: 10px; background: transparent; color: #A9B3C4; font: inherit; font-weight: 750; cursor: pointer; transition: color 0.16s ease, background 0.16s ease, border-color 0.16s ease; }
.wa-back .v-icon { color: inherit !important; }
.wa-back:hover { border-color: var(--wa-border); background: rgba(255, 255, 255, 0.045); color: #fff; }

.wa-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 22px;
  margin-bottom: 15px;
  padding: 17px 18px;
  border: 1px solid var(--wa-border);
  border-radius: 18px;
  background: linear-gradient(135deg, rgba(35, 40, 56, 0.96), rgba(29, 34, 48, 0.92));
  box-shadow: 0 22px 52px -43px rgba(0, 0, 0, 0.92);
}
.wa-heading { display: flex; min-width: 0; align-items: flex-start; gap: 14px; }
.wa-file-icon {
  --wa-file-color: #8EA7FF;
  display: flex;
  width: 52px;
  height: 52px;
  flex: 0 0 52px;
  align-items: center;
  justify-content: center;
  border: 1px solid color-mix(in srgb, var(--wa-file-color) 32%, transparent);
  border-radius: 16px;
  background: color-mix(in srgb, var(--wa-file-color) 13%, transparent);
}
.wa-file-icon .v-icon { color: var(--wa-file-color) !important; }
.wa-title-wrap { min-width: 0; flex: 1 1 auto; }
.wa-eyebrow { display: flex; align-items: center; gap: 7px; color: #8995A9; font-size: 9.5px; font-weight: 800; letter-spacing: 0.08em; text-transform: uppercase; }
.wa-eyebrow i { width: 3px; height: 3px; border-radius: 50%; background: #687386; }
.wa-title {
  margin: 5px 0 0;
  overflow: visible;
  color: #FFFFFF;
  font-size: clamp(22px, 2.5vw, 30px);
  font-weight: 880;
  letter-spacing: -0.04em;
  line-height: 1.13;
  text-overflow: initial;
  white-space: normal;
  overflow-wrap: anywhere;
}
.wa-meta { display: flex; gap: 13px; flex-wrap: wrap; margin: 9px 0 0; padding: 0; border: 0; color: #8F9AAF; font-size: 10.5px; }
.wa-meta span { display: inline-flex; align-items: center; gap: 5px; min-width: 0; }
.wa-meta .v-icon { color: #788498 !important; }
.wa-actions { display: flex; flex: 0 0 auto; align-items: center; gap: 8px; }
.wa-action { min-height: 40px !important; padding: 0 14px !important; border-radius: 11px !important; font-size: 11.5px !important; font-weight: 750 !important; letter-spacing: 0 !important; text-transform: none !important; }
.wa-action--primary { background: #5B7CFF !important; box-shadow: 0 12px 25px -17px rgba(91, 124, 255, 0.95); }
.wa-action--secondary { border-color: rgba(126, 151, 255, 0.26) !important; color: #BFCBFF !important; }
.wa-action--danger { border-color: rgba(255, 115, 115, 0.26) !important; color: #FF9292 !important; }

.wa-body { display: grid; grid-template-columns: minmax(0, 1fr) 350px; align-items: start; gap: 16px; min-height: 70vh; }
.wa-viewer-col {
  display: flex;
  min-width: 0;
  min-height: 72vh;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid var(--wa-border);
  border-radius: 18px;
  background: #1B202D;
  box-shadow: 0 25px 54px -45px rgba(0, 0, 0, 0.94);
}
.wa-viewer-toolbar { display: flex; min-height: 60px; align-items: center; justify-content: space-between; gap: 14px; padding: 9px 13px; border-bottom: 1px solid var(--wa-border); background: rgba(35, 40, 56, 0.94); }
.wa-viewer-file { display: flex; min-width: 0; align-items: center; gap: 9px; }
.wa-viewer-file > span { display: flex; width: 34px; height: 34px; flex: 0 0 34px; align-items: center; justify-content: center; border-radius: 10px; background: rgba(91, 124, 255, 0.12); }
.wa-viewer-file > span .v-icon { color: #7896FF !important; }
.wa-viewer-file div { min-width: 0; }
.wa-viewer-file small { display: block; color: #9AA5B7; font-size: 9.5px; font-weight: 750; letter-spacing: 0.07em; text-transform: uppercase; }
.wa-viewer-file strong { display: block; max-width: min(520px, 48vw); margin-top: 2px; overflow: hidden; color: #DDE3EC; font-size: 11.5px; text-overflow: ellipsis; white-space: nowrap; }
.wa-viewer-status { display: inline-flex; flex: 0 0 auto; align-items: center; gap: 7px; padding: 6px 9px; border: 1px solid rgba(66, 215, 135, 0.14); border-radius: 9px; background: rgba(66, 215, 135, 0.07); color: #9ADAB6; font-size: 9.5px; font-weight: 750; }
.wa-viewer-status span { width: 6px; height: 6px; border-radius: 50%; background: #42D787; box-shadow: 0 0 0 3px rgba(66, 215, 135, 0.11); }
.wa-pdf { width: 100%; min-height: calc(72vh - 60px); flex: 1 1 auto; }
.wa-image { display: flex; flex: 1; align-items: center; justify-content: center; overflow: auto; padding: 24px; background: #191E2B; }
.wa-image img { max-width: 100%; max-height: 82vh; border-radius: 10px; object-fit: contain; box-shadow: 0 20px 48px rgba(0, 0, 0, 0.28); }

.wa-state { display: flex; min-height: 430px; flex: 1; flex-direction: column; align-items: center; justify-content: center; gap: 7px; margin: 0; padding: 48px 24px; color: #8D98AA; text-align: center; }
.wa-state-orb { display: flex; width: 70px; height: 70px; align-items: center; justify-content: center; margin-bottom: 5px; border: 1px solid rgba(126, 151, 255, 0.2); border-radius: 22px; background: linear-gradient(145deg, rgba(91, 124, 255, 0.16), rgba(167, 139, 250, 0.07)); }
.wa-state-orb .v-icon { color: #7896FF !important; }
.wa-state--error .wa-state-orb { border-color: rgba(255, 115, 115, 0.2); background: rgba(255, 115, 115, 0.08); }
.wa-state--error .wa-state-orb .v-icon { color: #FF8585 !important; }
.wa-state > strong { color: #EBEFF6; font-size: 15px; }
.wa-state > span { max-width: 360px; color: #8994A7; font-size: 11.5px; line-height: 1.45; }

.wa-side {
  position: sticky;
  top: 16px;
  display: flex;
  max-height: calc(100vh - 98px);
  flex-direction: column;
  gap: 12px;
  padding: 14px;
  border: 1px solid var(--wa-border);
  border-radius: 18px;
  background: rgba(31, 36, 52, 0.94);
  box-shadow: 0 22px 52px -44px rgba(0, 0, 0, 0.94);
}
.wa-side-head { display: flex; align-items: center; justify-content: space-between; gap: 10px; padding: 1px 1px 12px; border-bottom: 1px solid var(--wa-border); }
.wa-side-head-title { display: flex; min-width: 0; align-items: center; gap: 10px; }
.wa-side-head-icon { display: flex; width: 38px; height: 38px; flex: 0 0 38px; align-items: center; justify-content: center; border-radius: 11px; background: rgba(91, 124, 255, 0.13); }
.wa-side-head-icon .v-icon { color: #7896FF !important; }
.wa-side-head-title div { min-width: 0; }
.wa-side-head-title small { display: block; color: #9AA5B7; font-size: 9.5px; font-weight: 750; letter-spacing: 0.07em; text-transform: uppercase; }
.wa-side-head-title strong { display: block; margin-top: 2px; color: #E9EDF5; font-size: 13px; }
.wa-side-count { display: flex; min-width: 29px; height: 26px; align-items: center; justify-content: center; padding: 0 8px; border-radius: 999px; background: rgba(91, 124, 255, 0.14); color: #8EA7FF; font-size: 10.5px; font-weight: 800; }
.wa-att-list { display: flex; max-height: none; min-height: 0; flex: 1; flex-direction: column; gap: 8px; margin: 0 -3px; padding: 0 3px; overflow-y: auto; scrollbar-color: #3D465C transparent; scrollbar-width: thin; }
.wa-att-empty { display: flex; min-height: 210px; flex-direction: column; align-items: center; justify-content: center; gap: 6px; padding: 24px 12px; color: #747F92; text-align: center; }
.wa-att-empty > span { display: flex; width: 54px; height: 54px; align-items: center; justify-content: center; margin-bottom: 3px; border-radius: 17px; background: rgba(255, 255, 255, 0.045); }
.wa-att-empty .v-icon { color: #7C879A !important; }
.wa-att-empty strong { color: #C6CDD9; font-size: 12px; }
.wa-att-empty small { max-width: 210px; color: #9AA5B7; font-size: 10.5px; line-height: 1.4; }
.wa-att {
  --wa-file-color: #8EA7FF;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px;
  border: 1px solid var(--wa-border);
  border-radius: 13px;
  background: #1B202D;
  transition: border-color 0.16s ease, background 0.16s ease, transform 0.16s ease;
}
.wa-att:hover { border-color: color-mix(in srgb, var(--wa-file-color) 38%, transparent); background: #202635; box-shadow: none; transform: translateY(-1px); }
.wa-att-row { display: flex; min-width: 0; align-items: center; gap: 6px; }
.wa-att-main { display: flex; min-width: 0; flex: 1; align-items: center; gap: 9px; padding: 0; border: 0; background: transparent; color: inherit; font: inherit; text-align: left; cursor: pointer; }
.wa-att-icon { display: flex; width: 38px; height: 38px; flex: 0 0 38px; align-items: center; justify-content: center; border: 1px solid color-mix(in srgb, var(--wa-file-color) 26%, transparent); border-radius: 11px; background: color-mix(in srgb, var(--wa-file-color) 11%, transparent); }
.wa-att-icon .v-icon { color: var(--wa-file-color) !important; }
.wa-att-text { display: block; min-width: 0; flex: 1; }
.wa-att-name { display: block; overflow: hidden; color: #E5E9F1; font-size: 11.5px; font-weight: 700; line-height: 1.35; text-overflow: ellipsis; white-space: nowrap; }
.wa-att-meta { display: flex; align-items: center; gap: 6px; margin-top: 3px; overflow: hidden; color: #9AA5B7; font-size: 9.5px; white-space: nowrap; }
.wa-att-meta span:first-child { color: var(--wa-file-color); font-weight: 800; letter-spacing: 0.05em; }
.wa-att-actions { display: flex; flex: 0 0 auto; align-items: center; gap: 1px; opacity: 0; transition: opacity 0.16s ease; }
.wa-att:hover .wa-att-actions,
.wa-att:focus-within .wa-att-actions { opacity: 1; }
.wa-att-actions ::v-deep .v-btn { width: 30px !important; height: 30px !important; color: #8792A5 !important; }
.wa-att-actions ::v-deep .v-btn:hover { background: rgba(255, 255, 255, 0.065) !important; color: #D9DFE9 !important; }
.wa-att-actions .wa-att-del:hover { color: #FF8B8B !important; }
.wa-att-desc { padding: 8px 9px; border: 1px solid rgba(91, 124, 255, 0.1); border-left: 3px solid #5B7CFF; border-radius: 8px; background: #171C28; color: #ADB7C7; font-size: 10.5px; line-height: 1.5; white-space: pre-wrap; }
.wa-att-edit { padding: 8px; border-radius: 9px; background: #181D29; }
.wa-att-edit ::v-deep .v-input__slot { background: #1C2230 !important; }
.wa-att-edit ::v-deep textarea { color: #E9EDF5 !important; font-size: 11.5px; }
.wa-att-edit ::v-deep fieldset { border-color: rgba(255, 255, 255, 0.1) !important; }
.wa-att-edit-actions { display: flex; gap: 7px; margin-top: 7px; }
.wa-att-add { min-height: 40px !important; margin-top: 0; border-radius: 11px !important; font-size: 11.5px !important; font-weight: 750 !important; letter-spacing: 0 !important; text-transform: none !important; }

.wa-dialog-card { overflow: hidden; border: 1px solid rgba(255, 255, 255, 0.09) !important; border-radius: 18px !important; background: #232838 !important; color: #F5F7FC !important; box-shadow: 0 28px 80px rgba(4, 7, 16, 0.6) !important; }
.wa-dialog-title { display: flex !important; min-width: 0; align-items: center; gap: 11px; padding: 16px 18px !important; border-bottom: 1px solid var(--wa-border); }
.wa-dialog-file { display: flex; width: 39px; height: 39px; flex: 0 0 39px; align-items: center; justify-content: center; border-radius: 12px; background: rgba(91, 124, 255, 0.14); }
.wa-dialog-file .v-icon { color: #7896FF !important; }
.wa-dialog-file--danger { background: rgba(255, 115, 115, 0.12); }
.wa-dialog-file--danger .v-icon { color: #FF8B8B !important; }
.wa-dialog-copy { display: flex; min-width: 0; flex-direction: column; }
.wa-dialog-copy small { color: #9AA5B7; font-size: 9.5px; font-weight: 750; letter-spacing: 0.07em; text-transform: uppercase; }
.wa-dialog-copy strong { max-width: 580px; margin-top: 2px; overflow: hidden; color: #F3F6FB; font-size: 14px; text-overflow: ellipsis; white-space: nowrap; }
.wa-dialog-head-actions { display: flex; flex: 0 0 auto; align-items: center; gap: 7px; margin-left: auto; }
.wa-dialog-head-actions ::v-deep .v-btn { border-radius: 10px; letter-spacing: 0; text-transform: none; }
.wa-dialog-footer { padding: 12px 16px 15px !important; border-top: 1px solid var(--wa-border); }
.wa-dialog-card ::v-deep .v-card__text { color: #B6C0D0 !important; }
.wa-preview-content { padding: 14px !important; background: #1A1F2C; }
.wa-preview-pdf { width: 100%; height: 72vh; border-radius: 10px; overflow: hidden; }
.wa-preview-img { display: flex; min-height: 50vh; align-items: center; justify-content: center; padding: 14px; border-radius: 10px; background: #171C27; }
.wa-preview-img img { max-width: 100%; max-height: 72vh; border-radius: 8px; object-fit: contain; }

.wa-back:focus-visible,
.wa-att-main:focus-visible { outline: 2px solid #8EA7FF; outline-offset: 2px; }

@media (max-width: 1120px) {
  .wa-header { align-items: flex-start; flex-direction: column; }
  .wa-actions { width: 100%; justify-content: flex-end; }
  .wa-body { grid-template-columns: minmax(0, 1fr) 320px; }
}

@media (max-width: 960px) {
  .wa-body { grid-template-columns: 1fr; }
  .wa-viewer-col { min-height: 60vh; }
  .wa-pdf { min-height: calc(60vh - 60px); }
  .wa-side { position: static; max-height: none; }
  .wa-att-list { max-height: none; overflow: visible; }
  .wa-att-actions { opacity: 1; }
}

@media (max-width: 680px) {
  .wa { margin: -16px; padding: 18px 14px 38px; }
  .wa-breadcrumb { margin-bottom: 8px; }
  .wa-header { gap: 16px; padding: 14px; border-radius: 16px; }
  .wa-heading { gap: 11px; }
  .wa-file-icon { width: 46px; height: 46px; flex-basis: 46px; border-radius: 14px; }
  .wa-title { font-size: 21px; }
  .wa-meta { gap: 8px 12px; }
  .wa-actions { display: grid; grid-template-columns: 1fr 1fr; gap: 7px; }
  .wa-action { width: 100%; }
  .wa-action--danger { grid-column: 1 / -1; }
  .wa-viewer-col,
  .wa-side { border-radius: 16px; }
  .wa-viewer-toolbar { min-height: 56px; padding: 8px 10px; }
  .wa-viewer-file strong { max-width: 54vw; }
  .wa-viewer-status { display: none; }
  .wa-state { min-height: 340px; }
  .wa-side { padding: 12px; }
  .wa-att-actions ::v-deep .v-btn { width: 34px !important; height: 34px !important; }
  .wa-preview-dialog .wa-dialog-title { align-items: flex-start; flex-wrap: wrap; }
  .wa-preview-dialog .wa-dialog-copy { flex: 1 1 calc(100% - 54px); }
  .wa-preview-dialog .wa-dialog-copy strong { max-width: 100%; }
  .wa-preview-dialog .wa-dialog-head-actions { width: 100%; margin-left: 0; padding-top: 10px; border-top: 1px solid var(--wa-border); }
  .wa-preview-dialog .wa-dialog-head-actions ::v-deep .v-btn:first-child { flex: 1 1 auto; }
}

@media (max-width: 440px) {
  .wa-breadcrumb > span:last-child { max-width: 42vw; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .wa-eyebrow span:last-child,
  .wa-eyebrow i { display: none; }
  .wa-actions { grid-template-columns: 1fr; }
  .wa-action--danger { grid-column: auto; }
  .wa-att-row { align-items: flex-start; flex-direction: column; }
  .wa-att-main { width: 100%; }
  .wa-att-actions { width: 100%; justify-content: flex-end; padding-top: 7px; border-top: 1px solid var(--wa-border); }
}

@media (hover: none) {
  .wa-att-actions { opacity: 1; }
}

@media (prefers-reduced-motion: reduce) {
  .wa *,
  .wa *::before,
  .wa *::after { scroll-behavior: auto !important; transition-duration: 0.01ms !important; animation-duration: 0.01ms !important; animation-iteration-count: 1 !important; }
}
</style>
